import type { MessageKey } from './es.ts';

/** English texts: the type forces every Spanish key to exist here. */
export const en: Record<MessageKey, string> = {
  'app.title': 'VOLT · Operations',
  'app.loading': 'Loading…',
  'app.error': 'Something went wrong',
  'app.retry': 'Retry',
  'app.save': 'Save',
  'app.cancel': 'Cancel',
  'app.confirm': 'Confirm',
  'app.close': 'Close',
  'app.search': 'Search',
  'app.none': 'No records',
  'app.yes': 'Yes',
  'app.no': 'No',
  'app.all': 'All',
  'app.actions': 'Actions',
  'app.reason': 'Reason',
  'app.reasonHelp': 'Recorded in the audit log',
  'app.refresh': 'Refresh',
  'app.language': 'Language',
  'app.signOut': 'Sign out',
  'app.role': 'Role',
  'app.status': 'Status',
  'app.created': 'Created',
  'app.updated': 'Updated',
  'app.details': 'Details',
  'app.more': 'More',
  'app.copy': 'Copy',
  'app.copied': 'Copied',
  'app.back': 'Back',
  'app.forbidden': 'You are not allowed to do this',
  'app.notFound': 'Not found',
  'app.theme': 'Theme',
  'app.theme.system': 'Theme: system',
  'app.theme.light': 'Theme: light',
  'app.theme.dark': 'Theme: dark',
  'session.state.REQUESTED': 'Requested',
  'session.state.AUTHORIZED': 'Authorized',
  'session.state.STARTING': 'Starting',
  'session.state.CHARGING': 'Charging',
  'session.state.SUSPENDED_EV': 'Paused by the vehicle',
  'session.state.SUSPENDED_EVSE': 'Paused by the charger',
  'session.state.STOPPING': 'Stopping',
  'session.state.ENDED': 'Ended',
  'session.state.SETTLED': 'Settled',
  'session.state.PAID': 'Paid',
  'session.state.FAILED': 'Failed',
  'session.state.EXPIRED': 'Expired',
  'session.state.CANCELLED': 'Cancelled',
  'payment.status.PAID': 'Paid',
  'payment.status.CAPTURED': 'Captured',
  'payment.status.APPROVED': 'Approved',
  'payment.status.SUCCEEDED': 'Approved',
  'payment.status.PENDING': 'Pending',
  'payment.status.AUTHORIZED': 'Authorized',
  'payment.status.FAILED': 'Failed',
  'payment.status.DECLINED': 'Declined',
  'payment.status.ERROR': 'Error',
  'payment.status.REFUNDED': 'Refunded',
  'payment.status.VOIDED': 'Voided',
  'payment.status.CANCELLED': 'Cancelled',
  'payment.status.NONE': 'No charge',
  'payment.status.OPEN': 'Open debt',
  'payment.status.WAIVED': 'Waived',
  'payment.status.WALLET': 'Balance',
  'severity.CRITICAL': 'Critical',
  'severity.WARNING': 'Warning',
  'severity.INFO': 'Info',
  'app.renderError':
    'This screen failed to render. The rest of the back-office is still available.',
  'app.renderRetry': 'Retry',
  'app.openInNewTab': 'Open in a new tab',

  'nav.dashboard': 'Now',
  'nav.sites': 'Sites',
  'nav.chargePoints': 'Chargers',
  'nav.sessions': 'Sessions',
  'nav.tariffs': 'Tariffs',
  'nav.simulator': 'Simulator',
  'nav.parameters': 'Parameters',
  'nav.payments': 'Payments',
  'nav.drivers': 'Drivers',
  'nav.alarms': 'Alarms',
  'nav.audit': 'Audit',
  'nav.staff': 'Staff',
  'nav.integrations': 'Integrations',

  'login.title': 'Sign in to the back-office',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.submit': 'Sign in',
  'login.forgot': 'I forgot my password',
  'login.resetSent': 'We sent you an email to reset your password',
  'login.tokenTitle': 'Lab: admin token',
  'login.token': 'Token (API_ADMIN_TOKEN)',
  'login.tokenActor': 'Actor (optional, e.g. staff:ana)',
  'login.tokenSubmit': 'Sign in with token',
  'login.noProvider': 'The API has no identity provider configured',
  'login.mfaCode': 'Authenticator code',
  'login.mfaTitle': 'Second factor',
  'login.mfaHelp': 'Enter the 6-digit code from your authenticator app',
  'login.verifyEmail': 'You must verify your email before signing in. Check your inbox.',
  'login.resendVerification': 'Resend verification email',
  'login.verificationSent': 'Verification email sent',
  'login.notInvited':
    'This email has no back-office invitation. Ask an administrator to invite you.',
  'login.disabled': 'Your account is disabled.',
  'login.reauth': 'Your session is too old. Please sign in again.',
  'login.wrongCredentials': 'Wrong email or password',
  'login.tooMany': 'Too many attempts. Wait a few minutes.',

  'mfa.title': 'Enable the second factor (TOTP)',
  'mfa.intro':
    'Your role requires a second factor. Scan the code with Google Authenticator, Authy or similar and enter the code it shows.',
  'mfa.secret': 'Manual key',
  'mfa.code': '6-digit code',
  'mfa.activate': 'Enable',
  'mfa.done': 'Second factor enabled. Sign in again to continue.',
  'mfa.reauthTitle': 'Confirm your password',
  'mfa.reauthHelp': 'For security, confirm your password to enable the second factor.',
  'mfa.notEnabled':
    'TOTP MFA is not enabled in Identity Platform for this project. Ask the Google Cloud administrator.',

  'dash.title': 'Now',
  'dash.chargePoints': 'Chargers',
  'dash.online': 'Online',
  'dash.offline': 'Offline',
  'dash.activeSessions': 'Active sessions',
  'dash.openAlarms': 'Open alarms',
  'dash.connectors': 'Connectors',
  'dash.map': 'Map',
  'dash.list': 'List',
  'dash.noSites': 'No sites yet. Create the first one under Sites.',
  'dash.lastSeen': 'Last seen',
  'dash.critical': 'critical',
  'dash.warning': 'warnings',

  'map.loading': 'Loading the map…',
  'map.noKey':
    'Google Maps is not configured in this environment (missing GOOGLE_MAPS_BROWSER_KEY). The list shows the same information.',
  'map.authFailure':
    'Google Maps rejected the key of this environment. Check the domain restriction of the key in Google Cloud.',
  'map.network': 'Google Maps could not be loaded. Check the connection and try again.',
  'map.open': 'Open site',
  'map.pickHelp':
    'Click on the map (or drag the marker) to set the site location; the coordinates fill in automatically.',

  'sites.title': 'Sites',
  'sites.new': 'New site',
  'sites.code': 'Code',
  'sites.name': 'Name',
  'sites.address': 'Address',
  'sites.city': 'City',
  'sites.latitude': 'Latitude',
  'sites.longitude': 'Longitude',
  'sites.timezone': 'Time zone',
  'sites.accessType': 'Access',
  'sites.chargePoints': 'Chargers',
  'sites.created': 'Site created',

  'cp.title': 'Chargers',
  'cp.new': 'New charger',
  'cp.chargeBoxId': 'Identity (chargeBoxId)',
  'cp.site': 'Site',
  'cp.vendor': 'Vendor',
  'cp.model': 'Model',
  'cp.serial': 'Serial',
  'cp.firmware': 'Firmware',
  'cp.lifecycle': 'Lifecycle',
  'cp.connected': 'Connected',
  'cp.disconnected': 'Disconnected',
  'cp.lastSeen': 'Last seen',
  'cp.connectors': 'Connectors',
  'cp.qr': 'QR',
  'cp.connector': 'Connector',
  'cp.standard': 'Standard',
  'cp.powerType': 'Type',
  'cp.maxPower': 'Max power',
  'cp.maxPowerKw': 'Max power (kW)',
  'cp.cabinetPower': 'Cabinet power',
  'cp.cabinetPowerKw': 'Cabinet power (kW)',
  'cp.cabinetPowerHelp':
    'Maximum power the charger delivers. When two connectors charge at once, the cabinet shares it between them (ADR 0026).',
  'cp.connectorPowerKw': 'Connector {n} (kW)',
  'cp.editPower': 'Edit power',
  'cp.powerSaved': 'Power updated',
  'cp.connectorsHelp':
    'One connector per outlet of the charger, with its standard (CCS2, CCS1, GB/T…), current type and maximum power in kW. The app shows them as entered.',
  'cp.template': 'Template',
  'cp.securityProfile': 'Security profile',
  'cp.heartbeat': 'Heartbeat (s)',
  'cp.addConnector': 'Add connector',
  'cp.removeConnector': 'Remove',
  'cp.created': 'Charger created. Issue its credential to install it.',
  'cp.tab.overview': 'Overview',
  'cp.tab.configuration': 'Configuration',
  'cp.tab.commands': 'Commands',
  'cp.tab.sessions': 'Sessions',
  'cp.tab.timeline': 'Timeline',
  'cp.tab.alarms': 'Alarms',
  'cp.commissioning': 'Commissioning',
  'cp.issueCredential': 'Issue credential',
  'cp.credentialTitle': 'Credential issued (shown only once)',
  'cp.credentialHelp':
    'Configure on the charger: user = identity, password = this key (Basic Auth, profile 2). CSMS URL:',
  'cp.credentialExpires': 'Expires',
  'cp.applyTemplate': 'Apply template',
  'cp.syncConfig': 'Read configuration (drift)',
  'cp.drift': 'Drifted',
  'cp.noDrift': 'No drift',
  'cp.key': 'Key',
  'cp.desired': 'Desired',
  'cp.observed': 'Observed',
  'cp.readonly': 'Read only',
  'cp.override': 'Change value',
  'cp.overrideValue': 'New value',
  'cp.lifecycleChange': 'Change state',
  'cp.lifecycleTo': 'New state',
  'cp.command': 'Command',
  'cp.sendCommand': 'Send command',
  'cp.commandPayload': 'Parameters (JSON)',
  'cp.commandResult': 'Result',
  'cp.commandHistory': 'Command history',
  'cp.commandSensitive': 'This command affects the charger operation. Confirm and leave a reason.',
  'cp.commandOk': 'Command sent',
  'cp.timelineKinds': 'Kinds',
  'cp.transactions': 'OCPP transactions',
  'cp.credential': 'Credential',
  'cp.credentialNone': 'No credential issued',
  'cp.credentialIssued': 'Issued',
  'cp.visibleInApp': 'Visible in the app',
  'cp.gatewayUnavailable': 'The OCPP gateway link is not configured in this environment',

  'lifecycle.INVENTORIED': 'Inventoried',
  'lifecycle.PROVISIONED': 'Provisioned',
  'lifecycle.CONNECTED_PENDING': 'Connected (pending)',
  'lifecycle.CONFIGURED': 'Configured',
  'lifecycle.TESTED': 'Tested',
  'lifecycle.OPERATIONAL': 'Operational',
  'lifecycle.MAINTENANCE': 'Maintenance',
  'lifecycle.REJECTED': 'Rejected',
  'lifecycle.DECOMMISSIONED': 'Decommissioned',

  'sessions.title': 'Sessions',
  'sessions.no': 'No.',
  'sessions.chargePoint': 'Charger',
  'sessions.evse': 'EVSE',
  'sessions.driver': 'Driver',
  'sessions.state': 'State',
  'sessions.started': 'Start',
  'sessions.ended': 'End',
  'sessions.energy': 'Energy',
  'sessions.cost': 'Cost',
  'sessions.payment': 'Payment',
  'sessions.channel': 'Channel',
  'sessions.stop': 'Stop',
  'sessions.cancel': 'Cancel session',
  'sessions.start': 'Start a test session',
  'sessions.startHelp': 'Sends RemoteStartTransaction as an operator (TEST or OPERATOR channel).',
  'sessions.events': 'Events',
  'sessions.costBreakdown': 'Cost breakdown',
  'sessions.costRunning': 'Running cost (estimate)',
  'sessions.costFinal': 'Final calculation',
  'sessions.costNone': 'No cost calculation for this session yet.',
  'sessions.computedAt': 'computed',
  'sessions.recalculate': 'Recalculate cost',
  'sessions.settle': 'Settle',
  'sessions.charge': 'Charge now',
  'sessions.receipt': 'Receipt',
  'sessions.receiptPdf': 'PDF receipt',
  'sessions.stopRequested': 'Stop requested',
  'sessions.filterState': 'State',
  'sessions.tariff': 'Tariff',
  'sessions.idle': 'Idle',
  'sessions.chargingTime': 'Charging time',
  'sessions.transaction': 'OCPP transaction',
  'sessions.runningCost': 'Running cost',
  'sessions.exposure': 'Exposure cap',
  'sessions.lines': 'Lines',
  'sessions.line.dimension': 'Item',
  'sessions.line.quantity': 'Quantity',
  'sessions.line.unitPrice': 'Unit price',
  'sessions.line.amount': 'Amount',
  'sessions.total': 'Total',
  'sessions.subtotal': 'Subtotal',
  'sessions.tax': 'Taxes',
  'sessions.discount': 'Discount',
  'sessions.dimension.ENERGY': 'Energy',
  'sessions.dimension.TIME': 'Charging time',
  'sessions.dimension.PARKING_TIME': 'Idle time',
  'sessions.dimension.FLAT': 'Session fee',
  'sessions.dimension.ADJUSTMENT': 'Adjustment',
  'sessions.dimension.CAP': 'Cap',
  'sessions.cap.minPrice': 'Minimum charge per connection',
  'sessions.cap.maxPrice': 'Price cap',

  'tariffs.title': 'Tariffs',
  'tariffs.new': 'New tariff',
  'tariffs.code': 'Code',
  'tariffs.codeHelp':
    'Uppercase letters, digits and dashes; identifies the tariff on receipts and audit.',
  'tariffs.name': 'Name',
  'tariffs.nameHelp': 'Commercial name the operator sees (e.g. VOLT tariff).',
  'tariffs.currency': 'Currency',
  'tariffs.versions': 'Versions',
  'tariffs.version': 'Version',
  'tariffs.published': 'Published',
  'tariffs.draft': 'Draft',
  'tariffs.retired': 'Retired',
  'tariffs.validFrom': 'Valid from',
  'tariffs.validTo': 'Valid to',
  'tariffs.open': 'Open-ended',
  'tariffs.publish': 'Publish',
  'tariffs.publishAt': 'Valid from (empty = now)',
  'tariffs.retire': 'Retire',
  'tariffs.newVersion': 'New version',
  'tariffs.definition': 'Definition (OCPI 2.2.1, JSON)',
  'tariffs.validate': 'Validate and simulate',
  'tariffs.valid': 'Valid definition',
  'tariffs.notes': 'Notes',
  'tariffs.taxIncluded': 'Prices include tax',
  'tariffs.assignments': 'Assignments',
  'tariffs.assignmentsHelp':
    'An assignment says which tariff each scope (platform, site, charger or connector) charges for a driver segment. Without a valid assignment a connector cannot start charging.',
  'tariffs.assignmentsWhere': 'Where this tariff applies right now.',
  'tariffs.assignmentHistory': 'Assignment history',
  'tariffs.noAssignments':
    'This tariff is not assigned to any scope: nobody is charged with it yet.',
  'tariffs.assign': 'Assign tariff',
  'tariffs.assigned': 'Tariff assigned',
  'tariffs.assignFrom': 'Valid from (empty = now)',
  'tariffs.scope': 'Scope',
  'tariffs.scopeId': 'Scope id',
  'tariffs.scopeTarget': 'Apply to',
  'tariffs.scope.PLATFORM': 'Whole platform',
  'tariffs.scope.TENANT': 'VOLT operator',
  'tariffs.scope.SITE': 'One site',
  'tariffs.scope.CHARGE_POINT': 'One charger',
  'tariffs.scope.CONNECTOR': 'One connector',
  'tariffs.scope.CONNECTOR_TYPE': 'One connector type',
  'tariffs.scopeHelp':
    'The most specific assignment wins (connector > charger > site > operator > platform); at equal scope and segment, the higher priority and then the most recent.',
  'tariffs.segment': 'Segment',
  'tariffs.segmentName.PUBLIC': 'PUBLIC: any driver',
  'tariffs.segmentName.INTERNAL': 'INTERNAL: internal use, no charge',
  'tariffs.segmentName.EMPLOYEE': 'EMPLOYEE: employees',
  'tariffs.segmentName.ADHOC': 'ADHOC: without account',
  'tariffs.segment.custom': 'Other (MEMBER:… or FLEET:…)',
  'tariffs.segmentHelp':
    'PUBLIC is the price for any driver and the fallback of the other segments.',
  'tariffs.priority': 'Priority',
  'tariffs.priorityHelp': 'Only matters between assignments of the same scope and segment.',
  'tariffs.end': 'End',
  'tariffs.resolve': 'Resolve the tariff of an EVSE',
  'tariffs.publishConfirm':
    'Publishing changes the price for new sessions from the given date. Sessions in progress keep their tariff.',
  'tariffs.bootstrap': 'Create the Volt base tariff',
  'tariffs.bootstrapHelp':
    'Creates VOLT-BASE with the agreed prices (ADR 0018), published and assigned to the whole platform. If it already exists nothing changes.',
  'tariffs.noTariffs': 'No tariffs yet. Create the Volt base tariff or a new one.',
  'tariffs.noActive':
    'No published version: connectors with this tariff cannot start charging until one is published.',
  'tariffs.noActiveShort': 'not published',
  'tariffs.activeNow': 'Current version',
  'tariffs.summary': 'Summary',
  'tariffs.status.DRAFT': 'Draft',
  'tariffs.status.SCHEDULED': 'Scheduled',
  'tariffs.status.ACTIVE': 'Active',
  'tariffs.status.RETIRED': 'Retired',
  'tariffs.status.SUPERSEDED': 'Superseded',
  'tariffs.audit': 'Tariff changes',
  'tariffs.elements': 'Elements',
  'tariffs.pricePerKwh': 'COP per kWh',
  'tariffs.idleFee': 'Idle fee',
  'tariffs.jsonInvalid': 'Invalid JSON',
  'tariffs.versionSaved': 'Version saved as a draft. Publish it when it is ready.',
  'tariffs.guideTitle': 'How are tariffs configured?',
  'tariffs.guide1':
    'Create a tariff with a code (e.g. VOLT-BASE) and a name. The tariff is the container; prices live in its versions.',
  'tariffs.guide2':
    'Inside the tariff create a version with the form: price per kWh (with time bands if you want), idle fee after charging, extra fees and tax. Validate and check the example before saving.',
  'tariffs.guide3':
    'Publish the version: from then on new sessions are charged with it and earlier ones keep their price. Every price change is a new version.',
  'tariffs.guide4':
    'Assign the tariff to the whole platform, a site, a charger or a connector, per driver segment. The most specific assignment wins.',
  'tariffs.editor.mode.form': 'Form',
  'tariffs.editor.mode.json': 'JSON (advanced)',
  'tariffs.editor.jsonOnly':
    'The latest version uses rules the form does not cover (kWh, power or duration tiers). It is edited as JSON.',
  'tariffs.editor.energy': 'Energy',
  'tariffs.editor.energyPrice': 'Price per kWh',
  'tariffs.editor.energyHelp':
    'Price that applies whenever no time band matches. Without tax unless you set a VAT.',
  'tariffs.editor.bandsHelp':
    'Optional time bands with a different price, in the local time of the site. A band that ends before it starts crosses midnight (20:00 to 05:00).',
  'tariffs.editor.from': 'From',
  'tariffs.editor.to': 'To',
  'tariffs.editor.days': 'Days',
  'tariffs.editor.days.ALL': 'Every day',
  'tariffs.editor.days.WEEKDAYS': 'Monday to Friday',
  'tariffs.editor.days.WEEKEND': 'Saturday and Sunday',
  'tariffs.editor.addBand': 'Add time band',
  'tariffs.editor.remove': 'Remove',
  'tariffs.editor.idle': 'Idle fee after charging',
  'tariffs.editor.idleEnabled': 'Charge idle time while the vehicle stays plugged in',
  'tariffs.editor.idleGrace': 'Courtesy minutes',
  'tariffs.editor.idlePrice': 'Price per minute',
  'tariffs.editor.idleMax': 'Cap of billable minutes (empty = no cap)',
  'tariffs.editor.idleStart': 'Idle time starts',
  'tariffs.editor.idleStart.EARLIEST':
    'When charging ends or the vehicle pauses it (whichever first)',
  'tariffs.editor.idleStart.TRANSACTION_END': 'Only when the transaction ends',
  'tariffs.editor.idleStart.SUSPENDED_EV': 'When the vehicle pauses charging',
  'tariffs.editor.extras': 'Other fees (optional)',
  'tariffs.editor.sessionFee': 'Flat fee per session',
  'tariffs.editor.timePrice': 'Price per minute of charging',
  'tariffs.editor.timeHelp': 'Added to the energy while charging. Empty = only energy is charged.',
  'tariffs.editor.tax': 'Tax and caps',
  'tariffs.editor.vat': 'VAT (%)',
  'tariffs.editor.vatHelp':
    'Empty = VAT-exempt service (ADR 0018). With a percentage, the receipt breaks it down.',
  'tariffs.editor.taxIncluded': 'The prices entered already include the tax',
  'tariffs.editor.minPrice': 'Minimum per session',
  'tariffs.editor.maxPrice': 'Maximum per session',
  'tariffs.editor.description': 'Description for the driver (optional)',
  'tariffs.editor.descriptionHelp': 'Short text the app shows next to the price.',
  'tariffs.editor.preview': 'Preview',
  'tariffs.editor.example': 'Example: 30 kWh in 45 minutes from 10:00 and 20 minutes idle',
  'tariffs.editor.exampleTotal': 'Example total',
  'tariffs.editor.validated': 'Valid definition',
  'tariffs.editor.saveDraft': 'Save draft',
  'tariffs.editor.invalidNumber':
    'Check the prices: numbers only, with a dot as decimal separator.',

  'sim.title': 'Price simulator',
  'sim.intro':
    'Compute the cost of a hypothetical session with a published tariff or a custom definition.',
  'sim.tariffVersion': 'Tariff version',
  'sim.startAt': 'Start',
  'sim.durationMin': 'Charging duration (min)',
  'sim.energyWh': 'Energy (Wh)',
  'sim.idleMin': 'Idle after charging (min)',
  'sim.powerW': 'Power (W)',
  'sim.run': 'Simulate',
  'sim.result': 'Result',

  'params.title': 'Parameters',
  'params.key': 'Key',
  'params.default': 'Default',
  'params.effective': 'Effective value',
  'params.scopes': 'Scopes',
  'params.values': 'Current values',
  'params.set': 'Set value',
  'params.scopeType': 'Scope',
  'params.scopeId': 'Scope id (uuid)',
  'params.value': 'Value (JSON)',
  'params.description': 'Description',
  'params.saved': 'Parameter saved',
  'params.restart': 'Requires restart',
  'params.sensitive': 'Admins only',

  'pay.title': 'Payments',
  'pay.payments': 'Charges',
  'pay.debts': 'Debts',
  'pay.webhooks': 'Webhooks',
  'pay.reconcile': 'Reconcile today',
  'pay.runJobs': 'Run pending charges',
  'pay.reference': 'Reference',
  'pay.amount': 'Amount',
  'pay.kind': 'Kind',
  'pay.status': 'Status',
  'pay.psp': 'PSP',
  'pay.session': 'Session',
  'pay.driver': 'Driver',
  'pay.reverse': 'Void or refund',
  'pay.registerVoid': 'Record void',
  'pay.resolveApproved': 'Mark approved',
  'pay.resolveFailed': 'Mark failed',
  'pay.reverseAmount': 'Amount to refund (empty = full)',
  'pay.reversed': 'Refund requested',
  'pay.debtAmount': 'Debt',
  'pay.attempts': 'Attempts',
  'pay.nextAttempt': 'Next attempt',
  'pay.payLink': 'Payment link',
  'pay.createLink': 'Create link',
  'pay.waive': 'Waive',
  'pay.retry': 'Retry charge',
  'pay.waived': 'Debt waived',
  'pay.event': 'Event',
  'pay.received': 'Received',
  'pay.outcome': 'Outcome',
  'pay.checksum': 'Checksum',
  'pay.unavailable': 'Payments are not configured in this environment',
  'pay.reconciled': 'Reconciliation executed',
  'pay.summary': 'Summary',
  'pay.period': 'Period',
  'pay.period.today': 'Today',
  'pay.period.7d': '7 days',
  'pay.period.month': 'This month',
  'pay.period.30d': '30 days',
  'pay.collected': 'Collected',
  'pay.refunded': 'Refunded',
  'pay.voided': 'voided',
  'pay.net': 'Net',
  'pay.pending': 'Pending',
  'pay.declined': 'Declined',
  'pay.errors': 'technical errors',
  'pay.openDebt': 'Open debt',
  'pay.billed': 'Billed in settled sessions',
  'pay.paidSessions': 'paid',
  'pay.byDay': 'By day',
  'pay.byMethod': 'By payment method',
  'pay.day': 'Day',
  'pay.count': 'Charges',
  'pay.method': 'Method',
  'pay.method.CARD': 'Card',
  'pay.method.WALLET': 'Nequi',
  'pay.method.LINK': 'Payment link',
  'pay.method.UNKNOWN': 'Other',
  'pay.provider': 'Wompi status',
  'pay.providerNone': 'No payment gateway configured in this environment.',
  'pay.env.sandbox': 'Wompi sandbox (test, no real money)',
  'pay.env.production': 'Production',
  'pay.env.fake': 'Emulator (no Wompi)',
  'pay.merchantOk': 'Gateway online ({latency} ms)',
  'pay.merchantFail': 'Gateway not responding: {error}',
  'pay.lastCapture': 'Last approved charge',
  'pay.lastWebhook': 'Last webhook received',
  'pay.webhooks24h': 'Webhooks in 24 h',
  'pay.applied': 'applied',
  'pay.invalidChecksum': 'with invalid checksum',
  'pay.otherOutcome': 'other',
  'pay.last24h': 'Charges in 24 h',
  'pay.approved': 'approved',
  'pay.declinedShort': 'declined',
  'pay.errorsShort': 'errors',
  'pay.pendingNow': 'Pending payments now',
  'pay.reconAlarms': 'Open reconciliation alarms',
  'pay.sourcesStuck': 'unconfirmed payment methods',
  'pay.never': 'Never',
  'pay.providerHelp':
    'How to know Wompi works: the connection responds, the day charges show as approved, every charge has its webhook applied and there are no reconciliation alarms. The automatic test against the sandbox runs on every push (Actions → CI). The money that actually reaches the account, with fees and withholdings, is in the Wompi dashboard.',
  'pay.from': 'From',
  'pay.to': 'To',
  'pay.pspReference': 'Wompi ref.',
  'pay.einvoicing': 'E-invoicing',
  'ein.title': 'Electronic invoicing (DIAN, Alegra)',
  'ein.help':
    'Each approved charge produces an electronic invoice and each refund a credit note, issued from the worker through Alegra. Issuing is switched on with the einvoicing.enabled parameter; by default only production-gateway charges are invoiced (einvoicing.sandbox_payments).',
  'ein.noProvider': 'No operational provider: {reason}',
  'ein.provider': 'Provider',
  'ein.enabled': 'Issuing on',
  'ein.disabled': 'Issuing off (einvoicing.enabled parameter)',
  'ein.since': 'Charges since',
  'ein.sandbox': 'Includes sandbox charges',
  'ein.sendEmail': 'Email to customer',
  'ein.lastAccepted': 'Last accepted',
  'ein.oldestPending': 'Oldest pending',
  'ein.check': 'Check account',
  'ein.checking': 'Checking the account…',
  'ein.checkOk': 'The account is ready to issue.',
  'ein.problems': 'Problems found',
  'ein.company': 'Company',
  'ein.numberTemplates': 'Numberings',
  'ein.electronic': 'electronic',
  'ein.notElectronic': 'not electronic',
  'ein.next': 'next',
  'ein.items': 'Items per line',
  'ein.itemMissing': 'no item',
  'ein.taxes': 'taxes',
  'ein.noTaxes': 'no taxes',
  'ein.finalConsumer': 'Final consumer',
  'ein.finalConsumerMissing': 'missing (created with the first invoice without a document)',
  'ein.run': 'Run cycle now',
  'ein.ran':
    'Cycle run: {enqueued} queued, {issued} issued, {accepted} accepted, {rejected} rejected, {errors} errors.',
  'ein.documents': 'Documents',
  'ein.kind': 'Kind',
  'ein.kind.INVOICE': 'Invoice',
  'ein.kind.CREDIT_NOTE': 'Credit note',
  'ein.status.PENDING': 'Pending',
  'ein.status.SENT': 'Sent',
  'ein.status.ACCEPTED': 'Accepted',
  'ein.status.REJECTED': 'Rejected',
  'ein.status.ERROR': 'Error',
  'ein.status.SKIPPED': 'Skipped',
  'ein.all': 'All',
  'ein.buyer': 'Buyer',
  'ein.number': 'Number',
  'ein.total': 'Total',
  'ein.attempts': 'Attempts',
  'ein.nextAttempt': 'Next attempt',
  'ein.error': 'Last error',
  'ein.retry': 'Retry',
  'ein.retryTitle': 'Retry issuing',
  'ein.retryHelp':
    'Queues the document again. If DIAN rejected it, a new one is issued with another number and the driver’s current document.',
  'ein.skip': 'Skip',
  'ein.skipTitle': 'Skip the document',
  'ein.skipHelp': 'It will not be issued. Give the reason (for example, a test charge).',
  'ein.cufe': 'CUFE',
  'ein.pdf': 'PDF',
  'ein.emailSent': 'Email sent',
  'ein.warnings': 'Warnings',
  'ein.session': 'Session',
  'ein.credentials': 'Credentials in use',
  'ein.schemeBasic': 'Basic authentication with the API token',
  'ein.schemeBearer': 'Bearer authentication with a JWT',
  'ein.tokenInfo': '{length}-character token ({shape})',
  'ein.tokenShape.jwt': 'JWT-shaped',
  'ein.tokenShape.token': 'classic token',
  'ein.tokenShape.suspicious': 'odd characters: copy it again',
  'ein.probeTitle': 'Try other credentials (not stored)',
  'ein.probeHelp':
    'Paste the email and token exactly as you have them in Alegra: the API tries them from the cloud without storing or logging them. If they work here but Check account fails, the Secret Manager values are different.',
  'ein.probeUser': 'Alegra email',
  'ein.probeToken': 'API token',
  'ein.probeRun': 'Try',
  'ein.probeResult': 'Result with the trial credentials (they were not stored).',
  'ein.compareTitle': 'Comparison with the stored secrets:',
  'ein.compareUser.exact': 'the email is the same',
  'ein.compareUser.case':
    'the email is the same except for letter case (store it again exactly as typed here)',
  'ein.compareUser.different':
    'the stored email is a different one (store this one as a new version of alegra-api-user)',
  'ein.compareTokenSame': 'the token is identical to the stored one',
  'ein.compareTokenDiff':
    'the stored token is different from this one ({length}): store this one as a new version of alegra-api-token',
  'ein.compareSameLength': 'same length',
  'ein.compareLengthDiff': 'length difference {delta}',
  'ein.setup': 'Setup',
  'ein.setupHelp':
    'First tap Check account to read the items and numberings from Alegra; then pick the charging service item (no VAT), save and turn on the switches. Everything is stored in the einvoicing.* parameters.',
  'ein.checkFirst': 'Tap Check account to see the items and numberings of the account.',
  'ein.item': 'Alegra item for the charge lines',
  'ein.itemHelp': 'It must be a service item without taxes. The id is shown in parentheses.',
  'ein.itemNone': 'No item (issuing will fail)',
  'ein.invoiceTemplate': 'Electronic invoice numbering',
  'ein.creditTemplate': 'Credit note numbering',
  'ein.preferred': 'The account’s preferred one',
  'ein.manualId': 'Manual id',
  'ein.save': 'Save setup',
  'ein.saved': 'Setup saved.',
  'ein.switches': 'Switches',
  'ein.toggleEnabled':
    'Issuing on: every approved charge produces a real electronic invoice before DIAN',
  'ein.toggleSandbox':
    'Include Wompi sandbox charges (only for the controlled test in staging; the documents are real)',
  'ein.enableTitle': 'Turn issuing on',
  'ein.enableHelp':
    'From now on every approved charge (and every refund) is sent to Alegra and DIAN. The documents are real and cannot be deleted; a mistake is corrected with a credit note.',
  'ein.disableTitle': 'Turn issuing off',
  'ein.disableHelp':
    'New charges will not be invoiced until it is turned on again; documents in progress finish.',
  'ein.sandboxTitle': 'Include sandbox charges',
  'ein.sandboxHelp':
    'Only to test the integration: a charge paid with a test card will produce a real electronic invoice before DIAN. Turn it off once the test is done.',
  'ein.sandboxOffTitle': 'Stop including sandbox charges',

  'drivers.title': 'Drivers',
  'drivers.new': 'New driver',
  'drivers.email': 'Email',
  'drivers.phone': 'Phone',
  'drivers.firstName': 'First name',
  'drivers.lastName': 'Last name',
  'drivers.phoneVerified': 'Mobile verified',
  'drivers.phoneUnverified': 'Mobile not verified',
  'drivers.name': 'Name',
  'drivers.billing': 'Billing',
  'drivers.block': 'Block',
  'drivers.unblock': 'Unblock',
  'drivers.paymentMethods': 'Payment methods',
  'drivers.sessions': 'Recent sessions',
  'drivers.debts': 'Debts',
  'drivers.segment': 'Segment',
  'drivers.document': 'Document',
  'drivers.wantsInvoice': 'Electronic invoice',
  'drivers.vehicle': 'Vehicle',
  'drivers.blocked': 'Blocked',
  'drivers.ok': 'In good standing',
  'drivers.blockedDebt': 'Blocked by debt',
  'drivers.blockedManual': 'Blocked manually',

  'alarms.title': 'Alarms',
  'alarms.kind': 'Kind',
  'alarms.severity': 'Severity',
  'alarms.chargePoint': 'Charger',
  'alarms.firstSeen': 'First seen',
  'alarms.lastSeen': 'Last seen',
  'alarms.occurrences': 'Times',
  'alarms.resolve': 'Resolve',
  'alarms.resolution': 'Resolution',
  'alarms.includeResolved': 'Include resolved',
  'alarms.resolved': 'Alarm resolved',

  'audit.title': 'Audit',
  'audit.when': 'When',
  'audit.actor': 'Who',
  'audit.action': 'Action',
  'audit.entity': 'Entity',
  'audit.outcome': 'Outcome',
  'audit.verify': 'Verify chain',
  'audit.chainOk': 'Chain intact',
  'audit.chainBroken': 'Chain broken at record',
  'audit.checked': 'records verified',
  'audit.filterAction': 'Action (prefix)',
  'audit.filterEntity': 'Entity id',
  'audit.filterActor': 'Actor',
  'audit.loadMore': 'Load more',

  'staff.title': 'Staff',
  'staff.invite': 'Invite',
  'staff.email': 'Email',
  'staff.name': 'Name',
  'staff.role': 'Role',
  'staff.sites': 'Sites (SITE_OWNER)',
  'staff.status': 'Status',
  'staff.linked': 'Identity linked',
  'staff.mfa': 'MFA',
  'staff.lastLogin': 'Last sign-in',
  'staff.disable': 'Disable',
  'staff.enable': 'Enable',
  'staff.invited':
    'Invitation created. The person signs in with that (verified) email in Identity Platform.',
  'staff.roleHelp.ADMIN': 'Everything: staff, tariffs, configuration, payments.',
  'staff.roleHelp.OPERATIONS':
    'Operations: chargers, commands, commissioning, sessions, alarms, parameters.',
  'staff.roleHelp.SUPPORT':
    'Support: sessions, RemoteStop and unlock, drivers, debts and refunds up to a limit.',
  'staff.roleHelp.READ_ONLY': 'Read only, including the audit log.',
  'staff.roleHelp.SITE_OWNER': 'Site owner: read only on their sites.',
  'staff.me': 'You',
  'staff.method': 'Access',

  'integrations.title': 'Integrations',
  'integrations.subtitle':
    'Keys for the public station status channel (/v1/public/stations): one per consumer, with optional allowed origins.',
  'integrations.create': 'New key',
  'integrations.label': 'Label',
  'integrations.labelHelp': 'Who it is for: "supercargadores.co website", "Mall screens".',
  'integrations.origins': 'Allowed origins',
  'integrations.originsHelp':
    'One per line, as https://domain (no path), up to 10. Leave empty to accept any origin; on a public web page the key is visible, so restrict the origin.',
  'integrations.anyOrigin': 'Any origin',
  'integrations.publicWarning':
    'A key inside a web page can be read by anyone who views the source. The real protection is the allowed origin, the per-key limit and revocation, not the secret. For a signage service that is not a browser, leave the origins empty.',
  'integrations.issuedTitle': 'Key created: {label}',
  'integrations.issuedHelp':
    'Copy it now: it will not be shown again. Send it in the Authorization: Bearer header (or X-Volt-Key) and, for EventSource, in ?key= on the URL.',
  'integrations.prefix': 'Prefix',
  'integrations.lastUsed': 'Last used',
  'integrations.neverUsed': 'Never used',
  'integrations.active': 'Active',
  'integrations.revoked': 'Revoked',
  'integrations.revoke': 'Revoke',
  'integrations.revokeHelp':
    'The website or screens using this key stop receiving data immediately (up to 30 seconds on other instances). It cannot be undone; create another key if needed.',
  'integrations.revokedMessage': 'Key revoked.',

  'status.Available': 'Available',
  'status.Preparing': 'Preparing',
  'status.Charging': 'Charging',
  'status.SuspendedEV': 'Suspended by EV',
  'status.SuspendedEVSE': 'Suspended by charger',
  'status.Finishing': 'Finishing',
  'status.Reserved': 'Reserved',
  'status.Unavailable': 'Unavailable',
  'status.Faulted': 'Faulted',
  'status.Offline': 'Offline',
};
